import { Link } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { PostType } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useArchivePostType, useNetwork } from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export function CMSMenu({ postType }: { postType: PostType }) {
  const { $t } = useI18n()
  const { mutateAsync: archive } = useArchivePostType()
  const { data: network } = useNetwork()
  const [canArchivePostType, canUpdatePostType] = hasScopesPermission(network, [
    'archivePostType',
    'updatePostType',
  ])

  if (!canArchivePostType && !canUpdatePostType) {
    return null
  }

  const archivePostType = async () => {
    try {
      const confirmed = await confirm({
        title: $t({
          defaultMessage: 'Are you sure you want to archive this model?',
          id: 'Admin.CMS.Archive.Confirm',
        }),
        description: $t({
          defaultMessage:
            'Archiving this model will prevent any future posts or replies from being created using this model. You can unarchive this model later.',
          id: 'Admin.CMS.Archive.Confirm.Description',
        }),
        proceedLabel: $t({
          defaultMessage: 'Archive',
          id: 'Generics.Archive',
        }),
        cancelLabel: $t({
          defaultMessage: 'Cancel',
          id: 'Generics.Cancel',
        }),
      })
      if (!confirmed) {
        return
      }

      await archive({ id: postType.id })
      toast({
        title: $t({
          defaultMessage: 'CMS model successfully archived',
          id: 'Admin.CMS.Model.Archived',
        }),
        status: 'success',
      })
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Could not archive CMS model',
          id: 'Admin.CMS.Model.Archived.Error',
        }),
        status: 'error',
      })
    }
  }

  return (
    <Dropdown placement="bottom-end">
      <Dropdown.ButtonMinimal
        as="div"
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <IconButton
          size="sm"
          variant="tertiaryNeutral"
          rounded
          icon={<SvgIcon name="dots-horizontal" />}
        />
      </Dropdown.ButtonMinimal>

      <Dropdown.Items>
        {canArchivePostType && !postType.archived && (
          <Dropdown.Item
            leadingIcon={<SvgIcon name="archive" />}
            onClick={e => {
              e.stopPropagation()
              archivePostType()
            }}
          >
            <T id="Generics.Archive" defaultMessage="Archive" />
          </Dropdown.Item>
        )}
        {canUpdatePostType && (
          <Dropdown.Item
            as={Link}
            leadingIcon={<SvgIcon name="cog" />}
            to={adminRoutes.site.cms.settings(postType.slug)}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <T id="Generics.Settings" defaultMessage="Settings" />
          </Dropdown.Item>
        )}
      </Dropdown.Items>
    </Dropdown>
  )
}
